import React, { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { Panel, Kpi, SevPill, SectionTitle, TimeAgo } from "@/components/sov";
import HardwareIntegrity from "@/components/HardwareIntegrity";
import LiveStrikeFeed from "@/components/LiveStrikeFeed";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import { Activity, AlertTriangle, ShieldAlert, Crosshair, Timer, Target, Server, Zap } from "lucide-react";

const SEV_COLORS = {
  critical: "#ff7a6e",
  high: "#f5a73a",
  medium: "#dcb95f",
  low: "#cdb98a",
  info: "#9bb1c8",
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="sov-panel" style={{ padding: "8px 12px" }}>
      <div className="font-mono text-[10px] text-gold-200 tracking-[0.18em] uppercase">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="font-mono text-[11px] text-ivory" style={{ color: p.color || p.fill }}>
          {p.name}: {p.value}
        </div>
      ))}
    </div>
  );
};

const Dashboard = () => {
  const [overview, setOverview] = useState(null);
  const [liveEvents, setLiveEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [ov, le] = await Promise.all([
        api.get("/dashboard/overview"),
        api.get("/dashboard/live-events?limit=18"),
      ]);
      setOverview(ov.data);
      setLiveEvents(le.data.events);
    } catch (e) {
      console.error("dashboard load failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  if (loading || !overview) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="dashboard-loading">
        <div className="text-center">
          <span className="dot-loader"><span /><span /><span /></span>
          <div className="mt-4 font-mono text-[10px] tracking-[0.3em] text-ivory-3 uppercase">
            Calibrating Vessel Sensors
          </div>
        </div>
      </div>
    );
  }

  const { kpis, severity_dist, timeline, top_sources } = overview;

  return (
    <div className="space-y-8" data-testid="dashboard">
      <SectionTitle sub="// SIEM · OPERATIONAL OVERWATCH">
        Command Bridge
      </SectionTitle>

      {/* KPI ROW */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5" data-testid="kpi-grid">
        <Kpi
          testid="kpi-events-24h"
          label="Events · 24H"
          value={kpis.events_24h.toLocaleString()}
          sub={`+${kpis.events_1h} in last hour`}
        />
        <Kpi
          testid="kpi-critical"
          label="Critical Signals"
          value={kpis.critical_24h}
          sub="elevated · review priority"
          accent
        />
        <Kpi
          testid="kpi-open-alerts"
          label="Open Alerts"
          value={kpis.open_alerts}
          sub={`${kpis.active_incidents} active incidents`}
        />
        <Kpi
          testid="kpi-risk-score"
          label="Risk Score"
          value={`${kpis.risk_score}/99`}
          sub={kpis.risk_score > 70 ? "⚜ heightened posture" : "⚜ steady posture"}
          accent
        />
      </div>

      {/* SECONDARY METRICS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <div className="sov-panel p-5 sov-corners" data-testid="metric-mttr">
          <span className="corner tl" /><span className="corner tr" />
          <span className="corner bl" /><span className="corner br" />
          <div className="flex items-center gap-3">
            <Timer size={20} className="text-gold-300" />
            <div>
              <div className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">MTTR</div>
              <div className="font-display text-[20px] text-gold-100 mt-0.5">
                {Math.floor(kpis.mttr_minutes / 60)}h {kpis.mttr_minutes % 60}m
              </div>
            </div>
          </div>
        </div>
        <div className="sov-panel p-5 sov-corners" data-testid="metric-hunts">
          <span className="corner tl" /><span className="corner tr" />
          <span className="corner bl" /><span className="corner br" />
          <div className="flex items-center gap-3">
            <Crosshair size={20} className="text-gold-300" />
            <div>
              <div className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Active Hunts</div>
              <div className="font-display text-[20px] text-gold-100 mt-0.5">{kpis.active_hunts}</div>
            </div>
          </div>
        </div>
        <div className="sov-panel p-5 sov-corners" data-testid="metric-incidents">
          <span className="corner tl" /><span className="corner tr" />
          <span className="corner bl" /><span className="corner br" />
          <div className="flex items-center gap-3">
            <ShieldAlert size={20} className="text-gold-300" />
            <div>
              <div className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Incidents</div>
              <div className="font-display text-[20px] text-gold-100 mt-0.5">{kpis.active_incidents}</div>
            </div>
          </div>
        </div>
        <div className="sov-panel p-5 sov-corners" data-testid="metric-velocity">
          <span className="corner tl" /><span className="corner tr" />
          <span className="corner bl" /><span className="corner br" />
          <div className="flex items-center gap-3">
            <Zap size={20} className="text-gold-300" />
            <div>
              <div className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Velocity</div>
              <div className="font-display text-[20px] text-gold-100 mt-0.5">
                {kpis.events_1h}/hr
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* HARDWARE INTEGRITY HUD */}
      <HardwareIntegrity />

      {/* LIVE STRIKE FEED — intake events in real-time */}
      <LiveStrikeFeed />

      {/* MAIN ROW: timeline + severity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Panel
          testid="panel-timeline"
          title="Event Telemetry · 24H"
          code="§ I.A"
          className="lg:col-span-2"
        >
          <div className="p-5 pb-2">
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={timeline}>
                <defs>
                  <linearGradient id="gold-grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#dcb95f" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#dcb95f" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="crit-grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ff7a6e" stopOpacity={0.7} />
                    <stop offset="100%" stopColor="#ff7a6e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(200,162,75,0.06)" vertical={false} />
                <XAxis
                  dataKey="hour"
                  stroke="#8d886f"
                  tick={{ fontSize: 9, fontFamily: "JetBrains Mono", letterSpacing: "0.1em" }}
                  axisLine={{ stroke: "rgba(200,162,75,0.2)" }}
                  tickLine={false}
                />
                <YAxis
                  stroke="#8d886f"
                  tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }}
                  axisLine={{ stroke: "rgba(200,162,75,0.2)" }}
                  tickLine={false}
                />
                <Tooltip content={<ChartTooltip />} />
                <Area
                  type="monotone"
                  dataKey="events"
                  stroke="#dcb95f"
                  strokeWidth={1.5}
                  fill="url(#gold-grad)"
                  name="Events"
                />
                <Area
                  type="monotone"
                  dataKey="critical"
                  stroke="#ff7a6e"
                  strokeWidth={1.5}
                  fill="url(#crit-grad)"
                  name="Critical"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel testid="panel-severity" title="Severity Distribution" code="§ I.B">
          <div className="p-5">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={severity_dist}
                  dataKey="count"
                  nameKey="severity"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  stroke="#0c0c11"
                  strokeWidth={2}
                >
                  {severity_dist.map((entry) => (
                    <Cell key={entry.severity} fill={SEV_COLORS[entry.severity]} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1.5 mt-2">
              {severity_dist.map((s) => (
                <div key={s.severity} className="flex items-center justify-between" data-testid={`sev-row-${s.severity}`}>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2" style={{ background: SEV_COLORS[s.severity] }} />
                    <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-ivory-2">
                      {s.severity}
                    </span>
                  </div>
                  <span className="font-mono text-[12px] text-gold-100">{s.count}</span>
                </div>
              ))}
            </div>
          </div>
        </Panel>
      </div>

      {/* LIVE EVENTS + TOP SOURCES */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Panel
          testid="panel-live-events"
          title="Live Telemetry Stream"
          code="§ I.C"
          className="lg:col-span-2 scan-line"
          action={
            <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-ivory-3 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#6cc88f] animate-pulse" />
              streaming
            </span>
          }
        >
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            <table className="sov-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Sev</th>
                  <th>Source</th>
                  <th>Event</th>
                  <th>Asset</th>
                  <th>Origin</th>
                </tr>
              </thead>
              <tbody>
                {liveEvents.map((e) => (
                  <tr key={e.id} data-testid={`live-event-${e.id.slice(0,6)}`}>
                    <td><TimeAgo iso={e.timestamp} /></td>
                    <td><SevPill severity={e.severity} /></td>
                    <td className="text-ivory-2">{e.source}</td>
                    <td className="text-gold-100">{e.event_type}</td>
                    <td className="text-ivory-2">{e.asset}</td>
                    <td className="text-ivory-3">{e.country}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel testid="panel-top-sources" title="Top Sources" code="§ I.D">
          <div className="p-5">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={top_sources} layout="vertical" margin={{ left: 0 }}>
                <CartesianGrid stroke="rgba(200,162,75,0.06)" horizontal={false} />
                <XAxis
                  type="number"
                  stroke="#8d886f"
                  tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }}
                  axisLine={{ stroke: "rgba(200,162,75,0.2)" }}
                />
                <YAxis
                  dataKey="name"
                  type="category"
                  stroke="#cfc6ad"
                  width={150}
                  tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
                  axisLine={{ stroke: "rgba(200,162,75,0.2)" }}
                  tickLine={false}
                />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(200,162,75,0.06)" }} />
                <Bar dataKey="count" fill="#c8a24b" radius={[0, 0, 0, 0]} name="Events" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>
    </div>
  );
};

export default Dashboard;
